import type { FormId } from "@@/shared/form/form-and-field-id";

import { DynamicIcon } from "@@/shared/icons/dynamic-icon";
import type { AppTheme } from "@@/styles/theme";
import type { Interpolation } from "@emotion/react";
import { faQuestion } from "@fortawesome/pro-regular-svg-icons";
import type {
    DeepKeysOf,
    IconName,
    Padding,
    Translatable,
} from "@towni/common";
import type { infer as InferZodType, ZodSchema } from "zod";
import { useForm3Components } from "./form3-components";
import { useForm3Field } from "./form3.use-form-field";

type ValueType = IconName;

type Props<FormData, FieldId extends DeepKeysOf<FormData>> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly description?: Translatable;
    readonly hideDescriptionAfterInput?: boolean;
    readonly label?: Translatable;
    readonly labelDescription?: Translatable;
    readonly disabled?: boolean;
    readonly spin?: boolean;
    readonly containerCss?: Interpolation<AppTheme>;
    readonly inputCss?: Interpolation<AppTheme>;
    readonly padding?: Padding;
    readonly onChange?: (newValue: ValueType | undefined) => void;
};

const createForm3IconNameInputComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3IconNameInput = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FormData, FieldId>,
    ) => {
        const FormComponents = useForm3Components<FormSchema, FormData>();
        const field = useForm3Field<
            FormSchema,
            InferZodType<FormSchema>,
            FieldId,
            ValueType
        >({
            fieldId: props.fieldId,
            formId: props.formId,
        });
        return (
            <FormComponents.TextInput<IconName>
                {...props}
                preElement={
                    <DynamicIcon
                        icon={field.value || ("fa-question" as IconName)}
                        fixedWidth
                        size={1}
                        css={{ marginRight: 10 }}
                        fallback={faQuestion}
                    />
                }
            />
        );
    };
    return Form3IconNameInput;
};

export { createForm3IconNameInputComponent };
